import type { ITranslationStorage, LANGUAGE_CODE } from '@monofe/util';

export enum TranslationKey {
    DELETE_COMPLETE = 'Tour removal completed',
    DELETE_FAILURE = 'Tour removal error (%@)',
    DELETE_POPUP_DESCRIPTION = "Do you want to delete '%@'. (%@)",
    DELETE_POPUP_ACCEPT = 'Delete',
    DELETE_POPUP_DECLINE = 'Cancel',
}

export type TranslateSubscriberCallback = (
    module: IModuleTranslate,
    newLanguage: LANGUAGE_CODE,
) => void;

export interface IModuleTranslate {
    translate: (key: TranslationKey, params?: string[]) => string;
    updateLanguage: (languageCode: LANGUAGE_CODE) => void;
    language: () => LANGUAGE_CODE;
    subscribeLanguageChanged: (cb: TranslateSubscriberCallback) => void;
    unsubscribeLanguageChanged: (cb: TranslateSubscriberCallback) => void;
    updateDefaultParams: (key: TranslationKey, params: string[]) => void;
    subscribeTranslationChanged: (key: TranslationKey, cb: TranslateSubscriberCallback) => void;
    unsubscribeTranslationChanged: (key: TranslationKey, cb: TranslateSubscriberCallback) => void;
}

const DEFAULT_PARAMS: Record<TranslationKey, string[]> = {
    [TranslationKey.DELETE_COMPLETE]: [],
    [TranslationKey.DELETE_FAILURE]: [],
    [TranslationKey.DELETE_POPUP_DESCRIPTION]: ['site'],
    [TranslationKey.DELETE_POPUP_ACCEPT]: [],
    [TranslationKey.DELETE_POPUP_DECLINE]: [],
};

function ModuleTranslator(translator: ITranslationStorage): IModuleTranslate {
    let activeLanguage: LANGUAGE_CODE = 'en';
    const languageChangeCallbacks: TranslateSubscriberCallback[] = [];
    const translationChangeCallbacks: Partial<
        Record<TranslationKey, TranslateSubscriberCallback[]>
    > = {};
    const defaultParams = { ...DEFAULT_PARAMS };

    const module: IModuleTranslate = {
        translate: (key: TranslationKey, params?: string[]) =>
            translator.translation(activeLanguage, key, params ?? defaultParams[key]),
        updateLanguage: (languageCode: LANGUAGE_CODE) => {
            activeLanguage = languageCode;
            languageChangeCallbacks.forEach(cb => {
                cb(module, activeLanguage);
            });
        },
        language: () => activeLanguage,
        subscribeLanguageChanged: (cb: TranslateSubscriberCallback) =>
            languageChangeCallbacks.push(cb),
        unsubscribeLanguageChanged: (cb: TranslateSubscriberCallback) => {
            const idx = languageChangeCallbacks.indexOf(cb);
            if (idx >= 0) {
                languageChangeCallbacks.splice(idx, 1);
            }
        },
        updateDefaultParams: (key: TranslationKey, params: string[]) => {
            defaultParams[key] = params;

            const callbacks = translationChangeCallbacks[key] ?? [];

            callbacks.forEach(cb => {
                cb(module, activeLanguage);
            });
        },
        subscribeTranslationChanged: (key: TranslationKey, cb: TranslateSubscriberCallback) => {
            const arr = translationChangeCallbacks[key] ?? [];
            arr.push(cb);
            translationChangeCallbacks[key] = arr;
        },
        unsubscribeTranslationChanged: (key: TranslationKey, cb: TranslateSubscriberCallback) => {
            const arr = translationChangeCallbacks[key] ?? [];
            const idx = arr.indexOf(cb);
            if (idx >= 0) {
                arr.splice(idx, 1);
            }
            translationChangeCallbacks[key] = arr;
        },
    };

    return module;
}

export default ModuleTranslator;
